import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { useThemeName, Box, Stack, Columns, Column, Heading, Text } from "braid-design-system";
import compact from "lodash/compact.js";
import { translations } from "../../../components/.vocab/index.mjs";
import { BrandLogo } from "../BrandLogo/BrandLogo.mjs";
import { Badges } from "./Badges/Badges.mjs";
import { NeedsUpdateBadge } from "./Badges/NeedsUpdateBadge.mjs";
import { Hero } from "./Hero/Hero.mjs";
import { root, selectable, isSelected, overflowHidden, topBorderRadius, bottomBorderRadius, brandLogo, title } from "./BrandCard.css.mjs";
const defaultBadge = {
  tone: "neutral",
  children: "Default",
  id: "1"
};
const onLiveAdBadge = {
  tone: "info",
  children: "On live ad",
  id: "2"
};
const getBadges = ({
  isDefault,
  isLiveOnAd
}) => compact([isDefault && defaultBadge, isLiveOnAd && onLiveAdBadge]);
const BrandCard = ({
  brand,
  onClick,
  selectable: selectable$1 = false,
  selected = false,
  showName = true,
  showBrandId = true,
  coverPlaceholderText,
  BrandControl = null,
  LogoRenderer = null,
  showCoverImage = true,
  showNeedsUpdateBadge = false
}) => {
  const { id, isDefault, isLiveOnAd, legacyId, name, assets } = brand || {};
  const badges = getBadges({ isDefault, isLiveOnAd });
  const theme = useThemeName();
  const { t } = useTranslations(translations);
  const placeholderText = showCoverImage ? coverPlaceholderText ?? t("No cover image") : t("Cover image is not included in this ad type.");
  const cover = showCoverImage ? assets == null ? void 0 : assets.cover : void 0;
  const hasBrandControl = BrandControl !== null;
  return /* @__PURE__ */ jsxs(
    Box,
    {
      cursor: onClick ? "pointer" : void 0,
      className: [
        root,
        {
          [selectable]: selectable$1,
          [isSelected]: selected,
          [overflowHidden]: !hasBrandControl
        }
      ],
      onClick,
      "data-testid": `bx-brand-card-${id}`,
      height: "full",
      width: "full",
      children: [
        showCoverImage && /* @__PURE__ */ jsx(
          Box,
          {
            className: {
              [topBorderRadius]: hasBrandControl && theme === "seekJobs"
            },
            children: /* @__PURE__ */ jsx(
              Hero,
              {
                id,
                cover,
                placeholderText,
                showCoverImage
              }
            )
          }
        ),
        showNeedsUpdateBadge && /* @__PURE__ */ jsx(NeedsUpdateBadge, {}),
        badges && /* @__PURE__ */ jsx(Badges, { badges }),
        /* @__PURE__ */ jsx(
          Box,
          {
            background: "surface",
            paddingX: "gutter",
            paddingY: "medium",
            className: {
              [bottomBorderRadius]: hasBrandControl
            },
            children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              /* @__PURE__ */ jsxs(Columns, { space: "gutter", alignY: "center", children: [
                /* @__PURE__ */ jsx(Column, { width: "3/4", children: LogoRenderer ? /* @__PURE__ */ jsx(LogoRenderer, {}) : /* @__PURE__ */ jsx(Box, { className: brandLogo, children: /* @__PURE__ */ jsx(BrandLogo, { logo: assets == null ? void 0 : assets.logo, placement: "brandCard" }) }) }),
                /* @__PURE__ */ jsx(Column, { children: BrandControl && /* @__PURE__ */ jsx(BrandControl, { brand }) })
              ] }),
              showName && /* @__PURE__ */ jsx(Heading, { level: "3", children: /* @__PURE__ */ jsx(
                Box,
                {
                  className: title,
                  "data-testid": `bx-brand-card-name-${id}`,
                  children: name
                }
              ) }),
              showBrandId && /* @__PURE__ */ jsx(Text, { size: "large", tone: "secondary", children: /* @__PURE__ */ jsx(Box, { "data-testid": `bx-brand-card-id-${id}`, children: t("ID {legacyId}", {
                legacyId: legacyId ? legacyId == null ? void 0 : legacyId.toString() : ""
              }) }) })
            ] })
          }
        )
      ]
    }
  );
};
export {
  BrandCard,
  getBadges
};
